import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import {
  agentlessQuery,
  saveAgentless,
  type CustomField,
  type DeviceAgentlessForm,
  type DeviceAgentlessPage,
} from '../api/devices'
import { unassignedDevice } from '../api/inventoree'
import { CheckField, FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Agentless settings - agentless/form_templates/defaultUpdate.php: aceleasi
 * campuri, aceleasi liste, acelasi POST (updateDevice), plus butonul
 * Unassigned de sub formular, care era in aceeasi macheta si pleaca la alt cap
 * (Inventoree::unassignedDevice). Ca in portal, butonul e stins cand masina nu
 * are niciun om pe ea - nu ai ce dezlega.
 *
 * ASEZAREA e cea comuna (`ui/FormGrid.tsx`). Inainte, cele douasprezece campuri
 * plus cele definite de firma stateau unul sub altul pe `form-horizontal`, cu
 * eticheta la stanga intr-un `<span class="color">` - pastila gri de 25px, cea
 * pe care numai `od_console.css` o stinge, si aia e aprinsa doar in Settings.
 * Bifa "Virtual machine" era casuta goala a browserului (13x13, albastrul lui):
 * in portal, `vm_switchery` o inlocuia cu un comutator desenat, dar pluginul
 * acela nu ruleaza in v4. Acum e bifa obisnuita a grilei, cu eticheta langa ea.
 *
 * CE NU S-A SCHIMBAT: campurile, ordinea, numele lor, ce se trimite si cine
 * poate apasa Unassigned.
 */
export function DeviceAgentless({ id }: ScreenProps) {
  const { data, error } = useQuery(agentlessQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Agentless page={data} />
}

function Agentless({ page }: { page: DeviceAgentlessPage }) {
  const query = useQueryClient()
  const [form, setForm] = useState(page.form)
  const [custom, setCustom] = useState<Record<string, string>>(() => valuesOf(page.customFields))
  useEffect(() => {
    setForm(page.form)
    setCustom(valuesOf(page.customFields))
  }, [page])
  const set = <K extends keyof DeviceAgentlessForm>(key: K, value: DeviceAgentlessForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const unassign = async () => {
    if (!confirm('Are you sure?')) return
    try {
      const saved = await unassignedDevice(page.device.id)
      if (saved.ok === 1) {
        toast('The device has been unassigned.', 'Success', 'success')
        await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agentless'] })
        await query.invalidateQueries({ queryKey: ['inventoree', 'table'] })
      } else {
        toast('An error occured! Please try again..', 'Error', 'error')
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Error', 'Error', 'error')
    }
  }

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!confirm('Are you sure?')) return
    const fields: Record<string, string> = {
      device_unique: String(page.device.id),
      device_name: form.name,
      device_type: form.typeId,
      device_subtype: form.subtypeId,
      device_owner: form.ownerId,
      device_office: form.officeId,
      device_group: form.groupId,
      device_brand: form.brandId,
      device_model: form.model,
      device_serial: form.serial,
      device_classified_info: form.classified,
    }
    if (form.virtual) fields.virtual_machine = '1'
    for (const [key, value] of Object.entries(custom)) fields[`custom_field_${key}`] = value
    try {
      await saveAgentless(page.device.id, fields)
      toast('Settings saved!', 'Success', 'success')
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agentless'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Error', 'Error', 'error')
    }
  }

  return (
    <div className="device-agentless">
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Update</strong> settings
        </h4>
      </header>
      <div className="panel-body">
        <form action="#" method="post" className="fv-form fv-form-bootstrap updateDevice" onSubmit={submit}>
          <input type="hidden" name="device_unique" value={page.device.id} required />

          <FormRow>
            <HalfField label="Name" required htmlFor="da_device_name">
              <input
                id="da_device_name"
                type="text"
                className="form-control"
                name="device_name"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </HalfField>
            <HalfField label="Device Type" htmlFor="da_device_type">
              <select
                id="da_device_type"
                name="device_type"
                className="form-control"
                value={form.typeId}
                onChange={(e) => set('typeId', e.target.value)}
              >
                <option value="">Select...</option>
                {page.types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{' '}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          {/*
            Sub-tipul se arata doar cand tipul ales are unul, ca in macheta
            veche. Cand lipseste, randul ramane cu un singur camp - marginile
            coloanelor sunt aceleasi, deci nu se muta nimic sub el.
          */}
          <FormRow>
            {page.subTypes.length > 0 && (
              <HalfField label="Device Sub-type" htmlFor="da_device_subtype">
                <select
                  id="da_device_subtype"
                  name="device_subtype"
                  className="form-control"
                  value={form.subtypeId}
                  onChange={(e) => set('subtypeId', e.target.value)}
                >
                  <option value="">Select...</option>
                  {page.subTypes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{' '}
                    </option>
                  ))}
                </select>
              </HalfField>
            )}
            <HalfField label="User" htmlFor="device_owner">
              <select
                name="device_owner"
                id="device_owner"
                className="form-control owner_list_autocomplete"
                value={form.ownerId}
                onChange={(e) => set('ownerId', e.target.value)}
              >
                <option value="">Select...</option>
                {page.owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField label="Office" required htmlFor="da_device_office">
              <select
                id="da_device_office"
                name="device_office"
                className="form-control selectContainer"
                required
                value={form.officeId}
                onChange={(e) => set('officeId', e.target.value)}
              >
                <option value="">Select...</option>
                {page.offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Group" htmlFor="da_device_group">
              <select
                id="da_device_group"
                name="device_group"
                className="form-control"
                value={form.groupId}
                onChange={(e) => set('groupId', e.target.value)}
              >
                <option value="">Select...</option>
                {page.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormRow>
            <CheckField
              id="virtual_machine"
              name="virtual_machine"
              value="1"
              className="vm_switchery"
              label="Virtual machine"
              checked={form.virtual}
              onChange={(on) => set('virtual', on)}
            />
          </FormRow>

          <FormRow>
            <HalfField label="Brand" htmlFor="da_device_brand">
              <select
                id="da_device_brand"
                name="device_brand"
                className="form-control"
                value={form.brandId}
                onChange={(e) => set('brandId', e.target.value)}
              >
                <option value="">Select...</option>
                {page.brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Model" required htmlFor="da_device_model">
              <input
                id="da_device_model"
                type="text"
                className="form-control"
                name="device_model"
                required
                value={form.model}
                onChange={(e) => set('model', e.target.value)}
              />
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField label="Serial" htmlFor="da_device_serial">
              <input
                id="da_device_serial"
                type="text"
                className="form-control"
                name="device_serial"
                value={form.serial}
                onChange={(e) => set('serial', e.target.value)}
              />
            </HalfField>
            <HalfField label="Classified info" htmlFor="da_device_classified_info">
              <textarea
                id="da_device_classified_info"
                className="form-control"
                name="device_classified_info"
                value={form.classified}
                onChange={(e) => set('classified', e.target.value)}
              />
            </HalfField>
          </FormRow>

          {/* Campurile firmei, doua pe rand, ca restul grilei. */}
          {pairs(page.customFields).map(([left, right]) => (
            <FormRow key={left.id}>
              <CustomHalf field={left} value={custom} onSet={setCustom} />
              {right ? <CustomHalf field={right} value={custom} onSet={setCustom} /> : null}
            </FormRow>
          ))}

          <FormActions>
            <button type="submit" className="btn btn-theme">
              Update
            </button>
          </FormActions>
        </form>

        {/*
          Unassigned a stat dintotdeauna in afara formularului - alt cap, alta
          intrebare - si ramane acolo. Sta insa pe aceeasi margine ca butonul
          Update de deasupra, nu decalat cu trei coloane ca inainte.
        */}
        <FormActions>
          <section className="settings">
            <button
              id="unassigned"
              className="btn btn-theme unassigned-device"
              data-id={page.device.id}
              disabled={form.ownerId === '' || form.ownerId === '0'}
              onClick={unassign}
            >
              Unassigned
            </button>
          </section>
        </FormActions>
        <div className="notifications-box"></div>
      </div>
    </div>
  )
}

function CustomHalf({
  field,
  value,
  onSet,
}: {
  field: CustomField
  value: Record<string, string>
  onSet: (next: (prev: Record<string, string>) => Record<string, string>) => void
}): ReactNode {
  const name = `custom_field_${field.id}`
  return (
    <HalfField label={field.label} htmlFor={name}>
      {field.field_type === 2 ? (
        <select
          id={name}
          className="form-control"
          name={name}
          value={value[String(field.id)] ?? ''}
          onChange={(e) => onSet((p) => ({ ...p, [field.id]: e.target.value }))}
        >
          <option value="">Select...</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          className="form-control"
          type="text"
          name={name}
          value={value[String(field.id)] ?? ''}
          onChange={(e) => onSet((p) => ({ ...p, [field.id]: e.target.value }))}
        />
      )}
    </HalfField>
  )
}

/** Campurile firmei, luate cate doua, ca sa cada pe cele doua coloane. */
function pairs(fields: CustomField[]): [CustomField, CustomField | undefined][] {
  const out: [CustomField, CustomField | undefined][] = []
  for (let i = 0; i < fields.length; i += 2) out.push([fields[i], fields[i + 1]])
  return out
}

function valuesOf(fields: CustomField[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of fields) out[String(field.id)] = field.value
  return out
}
